// MediaPreviewModal — #960 single-click PLAY / double-click EXPAND target.
// A single-click on a tile opens this in "play" mode (video/audio autoplay,
// no metadata clutter); a double-click opens "expand" mode (larger, with the
// file's name/type/size/date, no autoplay so it doesn't surprise-play audio).
// Images/documents have nothing to "play", so both modes just show the asset.
import React from "react";
import PropTypes from "prop-types";
import * as S from "./MediaLibrary.styled";
import { IconClose, IconDocument } from "./icons";
import { formatBytes, TYPE_LABEL } from "../../pages/MediaLibrary/helpers";

function shortDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
}

export default function MediaPreviewModal({ item, mode = "play", onClose }) {
  if (!item) return null;
  const autoPlay = mode === "play";
  const isExpand = mode === "expand";

  return (
    <S.Backdrop
      role="dialog"
      aria-modal="true"
      aria-label={`${isExpand ? "Expanded" : "Playing"} ${item.name}`}
      data-testid="media-preview-modal"
      data-mode={mode}
      onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
    >
      <S.Modal w={isExpand ? 760 : 620}>
        <S.ModalHead>
          <div>
            <div className="t" title={item.name}>{item.name}</div>
          </div>
          <button type="button" className="x" aria-label="Close" onClick={onClose}>
            <IconClose size={20} />
          </button>
        </S.ModalHead>
        <S.ModalBody>
          <S.PreviewStage>
            {item.type === "image" && (
              <img src={item.url || item.thumbnail} alt={item.name} />
            )}
            {item.type === "video" && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                key={item.url}
                src={item.url}
                poster={item.thumbnail || undefined}
                controls
                autoPlay={autoPlay}
                data-testid="media-preview-video"
              />
            )}
            {item.type === "audio" && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio
                key={item.url}
                src={item.url}
                controls
                autoPlay={autoPlay}
                data-testid="media-preview-audio"
              />
            )}
            {item.type === "document" && (
              <div style={{ color: "#C9D2E0", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 40 }}>
                <IconDocument size={48} />
                <a href={item.url} target="_blank" rel="noreferrer" style={{ color: "#C9D2E0" }}>
                  Open document
                </a>
              </div>
            )}
          </S.PreviewStage>
          {isExpand && (
            <S.PreviewMeta data-testid="media-preview-meta">
              <span><b>{TYPE_LABEL[item.type] || "File"}</b></span>
              <span>{formatBytes(item.size)}</span>
              {item.uploadedAt && <span>{shortDate(item.uploadedAt)}</span>}
            </S.PreviewMeta>
          )}
        </S.ModalBody>
        <S.ModalFoot>
          <S.GhostButton onClick={onClose}>Close</S.GhostButton>
        </S.ModalFoot>
      </S.Modal>
    </S.Backdrop>
  );
}

MediaPreviewModal.propTypes = {
  item: PropTypes.object,
  mode: PropTypes.oneOf(["play", "expand"]),
  onClose: PropTypes.func,
};
